import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Select from 'react-select';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

function ItemIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const history = useHistory();
    const { isAuthenticated, companyId, permissions, userId, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Master');
    const hasEditMasterPermission = permissions.includes('Edit Master');
    const hasDeleteMasterPermission = permissions.includes('Delete Master');
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdateLoading, setIsUpdateLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [errorMessageEdit, setErrorMessageEdit] = useState("");

    // Dropdown data
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uoms, setUoms] = useState([]);

    // Form fields
    const [Code, setCode] = useState('');
    const [Name, setName] = useState('');
    const [PackSize, setPackSize] = useState('');
    const [HSNCode, setHSNCode] = useState('');
    const [GSTRate, setGSTRate] = useState('');
    const [MinRequiredQty, setMinRequiredQty] = useState('');
    const [BarCode, setBarCode] = useState('');
    const [NoOfUnitPacking, setNoOfUnitPacking] = useState('');
    const [BrandId, setBrandId] = useState(null);
    const [CategoryId, setCategoryId] = useState(null);
    const [UOMId, setUOMId] = useState(null);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchtableData();
        fetchDropdownData();
        setLoading(true);
    }, [isAuthenticated, companyId, userId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchtableData = async () => {
        setLoading(true);
        fetchData('ItemApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
                console.log(response);
            })
            .catch((error) => {
                console.error('Error fetching Item Details:', error);
                if (error.response && error.response.status === 401) {
                    logout();
                    history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const fetchDropdownData = async () => {
        try {
            // Fetch Brands
            const brandResponse = await fetchData('ItemApi', `/Brand/${companyId}`, token);
            if (brandResponse.responseCode === '1111') {
                setBrands(brandResponse.response_data.map(item => ({
                    value: item.Id,
                    label: item.Name
                })));
            }

            // Fetch Categories
            const categoryResponse = await fetchData('ItemApi', `/Category/${companyId}`, token);
            if (categoryResponse.responseCode === '1111') {
                setCategories(categoryResponse.response_data.map(item => ({
                    value: item.Id,
                    label: item.Name
                })));
            }

            // Fetch UOMs
            const uomResponse = await fetchData('ItemApi', `/uom/${companyId}`, token);
            if (uomResponse.responseCode === '1111') {
                setUoms(uomResponse.response_data.map(item => ({
                    value: item.Id,
                    label: item.Name
                })));
            }
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    };

    const handleDelete = async () => {
        deleteData('ItemApi', `${deleteItemId}`, token)
            .then((response) => {
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const [isModalGrpOpen, setIsModalGrpOpen] = useState(false);
    const toggleModalGroup = () => {
        setIsModalGrpOpen(!isModalGrpOpen);
        setErrorMessage('');
    };

    const addGroupRecord = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        if (!Code || !Name || !BrandId || !CategoryId || !UOMId) {
            setErrorMessage('Code, Name, Brand, Category, and UOM are required!');
            setIsLoading(false);
            return;
        }

        const isDuplicate = data.some(
            (item) => item.Code.trim().toLowerCase() === Code.trim().toLowerCase()
        );

        if (isDuplicate) {
            setErrorMessage("This Item Code already exists!");
            setIsLoading(false);
            return;
        }

        const Item = {
            CompanyId: companyId,
            Code,
            Name,
            PackSize,
            HSNCode,
            GSTRate: parseFloat(GSTRate) || 0,
            MinRequiredQty: parseFloat(MinRequiredQty) || 0,
            BarCode,
            NoOfUnitPacking: parseFloat(NoOfUnitPacking) || 0,
            BrandId: BrandId.value,
            CategoryId: CategoryId.value,
            UOMId: UOMId.value,
            IsActive: true,
            CreatedBy: userId,
            CreatedDate: new Date(),
        };

        postData('ItemApi', Item, token)
            .then((response) => {
                console.log('Item Created success:', response);
                resetForm();
                setIsModalGrpOpen(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Item Create error:', error);
                setErrorMessage('Failed to create item. Please try again.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const resetForm = () => {
        setCode('');
        setName('');
        setPackSize('');
        setHSNCode('');
        setGSTRate('');
        setMinRequiredQty('');
        setBarCode('');
        setNoOfUnitPacking('');
        setBrandId(null);
        setCategoryId(null);
        setUOMId(null);
        setErrorMessage('');
    };

    // EDIT
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [updatedData, setUpdateData] = useState({});

    const openDetailModal = async (Id) => {
        try {
            const response = await fetchData('ItemApi', `/Item/${Id}`, token);
            if (response.responseCode === '1111') {
                const itemData = response.response_data;
                setUpdateData({
                    ...itemData,
                    BrandId: brands.find(b => b.value === itemData.BrandId),
                    CategoryId: categories.find(c => c.value === itemData.CategoryId),
                    UOMId: uoms.find(u => u.value === itemData.UOMId)
                });
                setIsDetailModalOpen(true);
            }
        } catch (error) {
            console.error('Error fetching Item Details:', error);
        }
    };

    const toggleModalGrp2 = () => {
        setIsDetailModalOpen(false);
        setErrorMessageEdit('');
    };

    const handleUpdateInputChange = (event) => {
        const { name, value } = event.target;
        setUpdateData((prevDataItem) => ({
            ...prevDataItem,
            [name]: value,
        }));
    };

    const handleUpdateSelectChange = (name, selectedOption) => {
        setUpdateData((prevDataItem) => ({
            ...prevDataItem,
            [name]: selectedOption,
        }));
    };

    const updateRecord = async (event) => {
        event.preventDefault();
        setIsUpdateLoading(true);

        if (!updatedData.Code || !updatedData.Name || !updatedData.BrandId || !updatedData.CategoryId || !updatedData.UOMId) {
            setErrorMessageEdit('Code, Name, Brand, Category, and UOM are required!');
            setIsUpdateLoading(false);
            return;
        }

        try {
            const dataToUpdate = {
                ...updatedData,
                BrandId: updatedData.BrandId.value,
                CategoryId: updatedData.CategoryId.value,
                UOMId: updatedData.UOMId.value,
                GSTRate: parseFloat(updatedData.GSTRate) || 0,
                MinRequiredQty: parseFloat(updatedData.MinRequiredQty) || 0,
                NoOfUnitPacking: parseFloat(updatedData.NoOfUnitPacking) || 0,
            };
            await updateData('ItemApi', `${updatedData.Id}`, dataToUpdate, token);
            window.location.reload();
            setIsDetailModalOpen(false);
            fetchtableData();
        } catch (error) {
            console.error('Error updating record:', error);
            setErrorMessageEdit('Failed to update item. Please try again.');
        } finally {
            setIsUpdateLoading(false);
        }
    };

    return (
        <div>
            {/* Modern Header Bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '20px',
                padding: '8px 12px',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
            }}>
                <h6 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#2d3748',
                    letterSpacing: '-0.3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <span style={{ fontSize: '14px' }}>📦</span>
                    ITEMS
                </h6>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder="Search items..."
                            value={searchQuery}
                            onChange={handleSearch}
                            onFocus={() => setIsFocused(true)}
                            onBlur={() => setIsFocused(false)}
                            style={{
                                width: isFocused ? '280px' : '220px',
                                height: '32px',
                                border: '2px solid',
                                borderColor: isFocused ? '#667eea' : '#e2e8f0',
                                borderRadius: '12px',
                                padding: '0 16px 0 40px',
                                fontSize: '14px',
                                transition: 'all 0.3s ease',
                                outline: 'none',
                                background: 'white',
                                boxShadow: isFocused ? '0 4px 12px rgba(102, 126, 234, 0.15)' : 'none',
                            }}
                        />

                    </div>

                    <Tooltip title="Add New Item" arrow placement="top">
                        <Button
                            onClick={toggleModalGroup}
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                fontWeight: '600',
                                height: '32px',
                                padding: '0 24px',
                                fontSize: '13px',
                                textTransform: 'none',
                                borderRadius: '12px',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                transition: 'all 0.3s ease',
                                border: 'none',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                            }}
                        >
                            + New Item
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <style>{`
                            .loader {
                                transform: rotateZ(45deg);
                                perspective: 1000px;
                                border-radius: 50%;
                                width: 48px;
                                height: 48px;
                                color: #fff;
                            }
                            .loader:before,
                            .loader:after {
                                content: '';
                                display: block;
                                position: absolute;
                                top: 0;
                                left: 0;
                                width: inherit;
                                height: inherit;
                                border-radius: 50%;
                                transform: rotateX(70deg);
                                animation: 1s spin linear infinite;
                            }
                            .loader:after {
                                color: #FF3D00;
                                transform: rotateY(70deg);
                                animation-delay: .4s;
                            }
                            @keyframes spin {
                                0%, 100% {
                                    box-shadow: .2em 0px 0 0px currentcolor;
                                }
                                12% {
                                    box-shadow: .2em .2em 0 0 currentcolor;
                                }
                                25% {
                                    box-shadow: 0 .2em 0 0px currentcolor;
                                }
                                37% {
                                    box-shadow: -.2em .2em 0 0 currentcolor;
                                }
                                50% {
                                    box-shadow: -.2em 0 0 0 currentcolor;
                                }
                                62% {
                                    box-shadow: -.2em -.2em 0 0 currentcolor;
                                }
                                75% {
                                    box-shadow: 0px -.2em 0 0 currentcolor;
                                }
                                87% {
                                    box-shadow: .2em -.2em 0 0 currentcolor;
                                }
                            }
                        `}</style>
                    <div className="loader"></div>
                    <span style={{ color: 'white', marginTop: '30px', fontSize: '16px', fontWeight: '500', letterSpacing: '0.5px' }}>
                        Loading...
                    </span>
                </div>
            )}

            {/* Create Modal */}
            {isModalGrpOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}>
                    <div
                        className="bg-white w-full overflow-hidden"
                        style={{
                            maxWidth: '900px',
                            maxHeight: '85vh',
                            borderRadius: '16px',
                            boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
                            animation: 'zoomIn 0.3s ease-out'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            //background: 'linear-gradient(135deg, #041815ff 0%, #045157ff 100%)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            height: '55px'
                        }}>
                            <div style={{ height: '20px' }}>
                                <h6 style={{
                                    color: 'white',
                                    fontSize: '15px',
                                    fontWeight: '700',
                                    margin: 0,
                                    letterSpacing: '-0.3px',
                                    marginTop: '-10px'
                                }}>
                                    New Item
                                </h6>
                                <p style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '13px',
                                    margin: '2px 0 0 0'
                                }}>
                                    Add a new Item to your system
                                </p>
                            </div>
                            <button
                                onClick={toggleModalGroup}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.2)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '36px',
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    color: 'white',
                                    fontSize: '22px',
                                    fontWeight: '300'
                                }}
                                onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                                onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                            >
                                ×
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 150px)', padding: '16px 20px' }}>
                            {errorMessage && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                                    border: '1px solid #fca5a5',
                                    borderRadius: '12px',
                                    padding: '16px',
                                    marginBottom: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    <span style={{ fontSize: '20px' }}>⚠️</span>
                                    <p style={{ color: '#991b1b', fontSize: '14px', margin: 0, fontWeight: '500' }}>{errorMessage}</p>
                                </div>
                            )}

                            <form onSubmit={addGroupRecord}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* First Line: Code and Name */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {/* Code */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Code <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={Code}
                                                onChange={(e) => setCode(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter Item code"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* Name - Takes 2 columns */}
                                        <div className="md:col-span-2">
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Name <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={Name}
                                                onChange={(e) => setName(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter Item name"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Second Line: 3 Dropdowns */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {/* Brand Dropdown */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Brand <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <Select
                                                value={BrandId}
                                                onChange={setBrandId}
                                                options={brands}
                                                placeholder="Select Brand"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderColor: '#e5e7eb',
                                                        '&:hover': { borderColor: '#667eea' }
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0px',
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>

                                        {/* Category Dropdown */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Category <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <Select
                                                value={CategoryId}
                                                onChange={setCategoryId}
                                                options={categories}
                                                placeholder="Select Category"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderColor: '#e5e7eb',
                                                        '&:hover': { borderColor: '#667eea' }
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0px',
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>

                                        {/* UOM Dropdown */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                UOM <span style={{ color: '#ef4444' }}>*</span>
                                            </label>
                                            <Select
                                                value={UOMId}
                                                onChange={setUOMId}
                                                options={uoms}
                                                placeholder="Select UOM"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: '32px',
                                                        height: '32px',
                                                        fontSize: '13px',
                                                        borderColor: '#e5e7eb',
                                                        '&:hover': { borderColor: '#667eea' }
                                                    }),
                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                        padding: '0 8px'
                                                    }),
                                                    input: (base) => ({
                                                        ...base,
                                                        margin: '0px',
                                                    }),
                                                    indicatorsContainer: (base) => ({
                                                        ...base,
                                                        height: '32px',
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Pack Size, HSN Code, GST Rate, Min Required Qty, Bar Code, No Of Unit Packing - All in one row */}
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">

                                        {/* Pack Size */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Pack Size
                                            </label>
                                            <input
                                                type="text"
                                                value={PackSize}
                                                onChange={(e) => setPackSize(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter pack size"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* HSN Code */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                HSN Code
                                            </label>
                                            <input
                                                type="text"
                                                value={HSNCode}
                                                onChange={(e) => setHSNCode(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter HSN code"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* GST Rate */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                GST Rate
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={GSTRate}
                                                onChange={(e) => setGSTRate(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter GST rate"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* Min Required Qty */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Min Required Qty
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={MinRequiredQty}
                                                onChange={(e) => setMinRequiredQty(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter min required qty"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* Bar Code */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                Bar Code
                                            </label>
                                            <input
                                                type="text"
                                                value={BarCode}
                                                onChange={(e) => setBarCode(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter bar code"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>

                                        {/* No Of Unit Packing */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                color: '#374151',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                marginBottom: '6px'
                                            }}>
                                                No Of Unit Packing
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={NoOfUnitPacking}
                                                onChange={(e) => setNoOfUnitPacking(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '7px 10px',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '13px',
                                                    transition: 'all 0.2s ease',
                                                    outline: 'none',
                                                    height: '32px'
                                                }}
                                                placeholder="Enter no of unit packing"
                                                onFocus={(e) => {
                                                    e.target.style.borderColor = '#667eea';
                                                    e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)';
                                                }}
                                                onBlur={(e) => {
                                                    e.target.style.borderColor = '#e5e7eb';
                                                    e.target.style.boxShadow = 'none';
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                            padding: '16px 24px',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            borderTop: '1px solid #e5e7eb'
                        }}>
                            <button
                                onClick={toggleModalGroup}
                                disabled={isLoading}
                                style={{
                                    padding: '8px 24px',
                                    background: 'white',
                                    border: '2px solid #e5e7eb',
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    opacity: isLoading ? 0.5 : 1
                                }}
                                onMouseEnter={(e) => !isLoading && (e.target.style.background = '#f9fafb')}
                                onMouseLeave={(e) => !isLoading && (e.target.style.background = 'white')}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addGroupRecord}
                                disabled={isLoading}
                                style={{
                                    padding: '8px 24px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: 'white',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                    opacity: isLoading ? 0.7 : 1
                                }}
                                onMouseEnter={(e) => !isLoading && (e.target.style.transform = 'translateY(-2px)')}
                                onMouseLeave={(e) => !isLoading && (e.target.style.transform = 'translateY(0)')}
                            >
                                {isLoading ? (
                                    <>
                                        <CircularProgress size={18} color="inherit" />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>✓</span>
                                        <span>Add Item</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modern Table */}
            <div style={{
                maxHeight: '520px',
                overflowY: 'auto',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                marginTop: '-15px'
            }}>
                <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>#</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Code</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Brand</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Category</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>UOM</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pack Size</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>HSN</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GST</TableCell>
                                <TableCell style={{ background: "linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%)", borderBottom: '2px solid #1e40af', color: 'white', fontWeight: '700', fontSize: '11px', padding: '2px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData && filteredData.map((item, index) => (
                                <TableRow
                                    key={item.Id}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                            transform: 'scale(1.001)',
                                            transition: 'all 0.2s ease'
                                        },
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', fontWeight: '600', color: '#667eea' }}>{index + 1}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#2d3748', fontWeight: '600' }}>{item.Code}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '14px', color: '#2d3748', fontWeight: '500' }}>{item.Name}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.Brand?.Name}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.Category?.Name}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.UOM?.Name}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.PackSize}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.HSNCode}</TableCell>
                                    <TableCell sx={{ padding: '3px', fontSize: '13px', color: '#4a5568' }}>{item.GSTRate}%</TableCell>
                                    <TableCell sx={{ padding: '3px', textAlign: 'center' }}>
                                        {hasEditMasterPermission && (
                                            <Tooltip title="Edit Item" arrow>
                                                <IconButton
                                                    onClick={() => openDetailModal(item.Id)}
                                                    sx={{
                                                        background: 'rgba(102, 126, 234, 0.1)',
                                                        marginRight: '8px',
                                                        '&:hover': {
                                                            background: 'rgba(102, 126, 234, 0.2)',
                                                            transform: 'scale(1.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <EditIcon style={{ color: '#110441ff', fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasDeleteMasterPermission && (
                                            <Tooltip title="Delete Item" arrow>
                                                <IconButton
                                                    onClick={() => openConfirmDialog(item.Id)}
                                                    sx={{
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        '&:hover': {
                                                            background: 'rgba(239, 68, 68, 0.2)',
                                                            transform: 'scale(1.1)'
                                                        },
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    <DeleteIcon style={{ color: 'brown', fontSize: '17px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Edit Modal */}
            {isDetailModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 p-3" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}>
                    <div className="bg-white w-full overflow-hidden" style={{ maxWidth: '900px', maxHeight: '85vh', borderRadius: '16px', boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)', animation: 'zoomIn 0.3s ease-out' }}>
                        <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', height: '55px', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <div style={{ height: '20px' }}>
                                <h6 style={{ color: 'white', fontSize: '15px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', marginTop: '-10px' }}>Edit Item</h6>
                                <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '13px', margin: '2px 0 0 0' }}>Update Item information</p>
                            </div>
                            <button onClick={toggleModalGrp2} style={{ background: 'rgba(255, 255, 255, 0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s ease', color: 'white', fontSize: '22px', fontWeight: '300' }} onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'} onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}>×</button>
                        </div>

                        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 150px)', padding: '16px 20px' }}>
                            {errorMessageEdit && (
                                <div style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', border: '1px solid #fca5a5', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ fontSize: '20px' }}>⚠️</span>
                                    <p style={{ color: '#991b1b', fontSize: '14px', margin: 0, fontWeight: '500' }}>{errorMessageEdit}</p>
                                </div>
                            )}

                            <form onSubmit={updateRecord}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                    {/* First Row: Code and Name */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Code <span style={{ color: '#ef4444' }}>*</span></label>
                                            <input type="text" name="Code" value={updatedData.Code || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter Item code" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Name <span style={{ color: '#ef4444' }}>*</span></label>
                                            <input type="text" name="Name" value={updatedData.Name || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter Item name" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                    </div>

                                    {/* Second Row: 6 fields in one line */}
                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Pack Size</label>
                                            <input type="text" name="PackSize" value={updatedData.PackSize || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter pack size" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>HSN Code</label>
                                            <input type="text" name="HSNCode" value={updatedData.HSNCode || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter HSN code" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>GST Rate</label>
                                            <input type="number" step="0.01" name="GSTRate" value={updatedData.GSTRate || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter GST rate" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Min Required Qty</label>
                                            <input type="number" step="0.01" name="MinRequiredQty" value={updatedData.MinRequiredQty || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter min required qty" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Bar Code</label>
                                            <input type="text" name="BarCode" value={updatedData.BarCode || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter bar code" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>No Of Unit Packing</label>
                                            <input type="number" step="0.01" name="NoOfUnitPacking" value={updatedData.NoOfUnitPacking || ''} onChange={handleUpdateInputChange} style={{ width: '100%', padding: '7px 10px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', transition: 'all 0.2s ease', outline: 'none', height: '32px' }} placeholder="Enter no of unit packing" onFocus={(e) => { e.target.style.borderColor = '#667eea'; e.target.style.boxShadow = '0 0 0 2px rgba(102, 126, 234, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
                                        </div>
                                    </div>

                                    {/* Third Row: 3 Dropdowns */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Brand <span style={{ color: '#ef4444' }}>*</span></label>
                                            <Select value={updatedData.BrandId} onChange={(option) => handleUpdateSelectChange('BrandId', option)} options={brands} placeholder="Select Brand" menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: (base) => ({ ...base, minHeight: '32px', height: '32px', fontSize: '13px', borderColor: '#e5e7eb', '&:hover': { borderColor: '#667eea' } }), valueContainer: (base) => ({ ...base, height: '32px', padding: '0 8px' }), input: (base) => ({ ...base, margin: '0px' }), indicatorsContainer: (base) => ({ ...base, height: '32px' }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Category <span style={{ color: '#ef4444' }}>*</span></label>
                                            <Select value={updatedData.CategoryId} onChange={(option) => handleUpdateSelectChange('CategoryId', option)} options={categories} placeholder="Select Category" menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: (base) => ({ ...base, minHeight: '32px', height: '32px', fontSize: '13px', borderColor: '#e5e7eb', '&:hover': { borderColor: '#667eea' } }), valueContainer: (base) => ({ ...base, height: '32px', padding: '0 8px' }), input: (base) => ({ ...base, margin: '0px' }), indicatorsContainer: (base) => ({ ...base, height: '32px' }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
                                        </div>
                                        <div>
                                            <label style={{ display: 'block', color: '#374151', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>UOM <span style={{ color: '#ef4444' }}>*</span></label>
                                            <Select value={updatedData.UOMId} onChange={(option) => handleUpdateSelectChange('UOMId', option)} options={uoms} placeholder="Select UOM" menuPortalTarget={document.body} menuPosition="fixed" styles={{ control: (base) => ({ ...base, minHeight: '32px', height: '32px', fontSize: '13px', borderColor: '#e5e7eb', '&:hover': { borderColor: '#667eea' } }), valueContainer: (base) => ({ ...base, height: '32px', padding: '0 8px' }), input: (base) => ({ ...base, margin: '0px' }), indicatorsContainer: (base) => ({ ...base, height: '32px' }), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }} />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div style={{ background: 'linear-gradient(to right, #f9fafb, #f3f4f6)', padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #e5e7eb' }}>
                            <button onClick={toggleModalGrp2} disabled={isUpdateLoading} style={{ padding: '8px 24px', background: 'white', border: '2px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#6b7280', cursor: isUpdateLoading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', opacity: isUpdateLoading ? 0.5 : 1 }} onMouseEnter={(e) => !isUpdateLoading && (e.target.style.background = '#f9fafb')} onMouseLeave={(e) => !isUpdateLoading && (e.target.style.background = 'white')}>Cancel</button>
                            <button onClick={updateRecord} disabled={isUpdateLoading} style={{ padding: '8px 24px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: 'white', cursor: isUpdateLoading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)', opacity: isUpdateLoading ? 0.7 : 1 }} onMouseEnter={(e) => !isUpdateLoading && (e.target.style.transform = 'translateY(-2px)')} onMouseLeave={(e) => !isUpdateLoading && (e.target.style.transform = 'translateY(0)')}>
                                {isUpdateLoading ? (<><CircularProgress size={18} color="inherit" /><span>Updating...</span></>) : (<><span>✓</span><span>Update Item</span></>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)',
                        minWidth: '400px'
                    }
                }}
            >
                <div style={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                    padding: '20px 24px',
                    color: 'white'
                }}>
                    <DialogTitle style={{
                        padding: 0,
                        fontSize: '18px',
                        fontWeight: '700',
                        color: 'white'
                    }}>
                        ⚠️ Confirm Delete
                    </DialogTitle>
                </div>
                <DialogContent style={{ padding: '24px', marginTop: '8px' }}>
                    <DialogContentText style={{
                        fontSize: '15px',
                        color: '#374151',
                        lineHeight: '1.6'
                    }}>
                        Are you sure you want to delete this Item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{
                    padding: '16px 24px',
                    background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
                    gap: '12px'
                }}>
                    <Button
                        onClick={closeConfirmDialog}
                        style={{
                            padding: '8px 24px',
                            background: 'white',
                            border: '2px solid #e5e7eb',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#6b7280',
                            textTransform: 'none',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDelete}
                        style={{
                            padding: '8px 24px',
                            background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                            border: 'none',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'white',
                            textTransform: 'none',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                        }}
                        onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                        onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default ItemIndex;
